import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Menu,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { apiFetch } from "../../api";

const normalizePhoneForGroup = (value) => String(value || "").replace(/\D/g, "").slice(-10);

const normalizeNameForGroup = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const personaGroupKey = (row) =>
  `${normalizePhoneForGroup(row?.telefono)}::${normalizeNameForGroup(row?.nombre)}`;

/**
 * Modal unificado para resumen, errores y resolución de conflictos de importación CSV.
 */
export default function CsvDuplicatesDialog({
  open,
  onClose,
  importResult = null,
  duplicates = [],
  onRefresh,
}) {
  const [rows, setRows] = React.useState([]);
  const [selectedActions, setSelectedActions] = React.useState({});
  const [loadingRows, setLoadingRows] = React.useState({});
  const [rowErrors, setRowErrors] = React.useState({});
  const [resolvingAll, setResolvingAll] = React.useState(false);

  // Download menu anchor
  const [downloadMenuAnchor, setDownloadMenuAnchor] = React.useState(null);

  // Modal de confirmación para "Es otra persona" (CREAR_PERSONA) individual
  const [confirmPersonaModal, setConfirmPersonaModal] = React.useState({
    open: false,
    rows: [],
    actionConfig: null,
  });

  // Modal de confirmación masiva para "Resolver seleccionados" con CREAR_PERSONA
  const [confirmBulkPersonaModal, setConfirmBulkPersonaModal] = React.useState({
    open: false,
    personaRows: [],
  });

  // Modal de confirmación al cerrar con conflictos pendientes
  const [confirmCloseModal, setConfirmCloseModal] = React.useState(false);

  const prevOpenRef = React.useRef(false);
  const refreshedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      refreshedRef.current = false;
      return;
    }

    if (prevOpenRef.current) return;
    prevOpenRef.current = true;
    refreshedRef.current = false;

    let initialRows = [];
    if (importResult && Array.isArray(importResult.resultados)) {
      initialRows = importResult.resultados.map((r) => ({ ...r }));
    } else if (Array.isArray(duplicates) && duplicates.length > 0) {
      initialRows = duplicates.map((d, idx) => ({
        fila: d.fila || idx + 2,
        estado: d.estado || "CONFLICTO_PENDIENTE",
        mensaje: d.mensaje || "Conflicto de teléfono",
        nombre: d.nombre || "",
        telefono: d.telefono || "",
        email: d.email || "",
        consulta: d.consulta || "",
        obtenido_de: d.obtenido_de || "",
        detalles: d.detalles || {},
      }));
    }

    setRows(initialRows);
    setLoadingRows({});
    setRowErrors({});
    setResolvingAll(false);

    // Preselección visual de acciones por fila
    const initialActions = {};
    initialRows.forEach((r) => {
      if (r.estado === "CONFLICTO_PENDIENTE") {
        const existentes = r.detalles?.clientes_existentes || [];
        const isSingle = existentes.length === 1 && r.detalles?.accion_recomendada === "AGREGAR_CONSULTA";

        if (isSingle && existentes.length === 1) {
          initialActions[r.fila] = {
            action: "AGREGAR_CONSULTA",
            cliente_id: existentes[0].id,
          };
        } else {
          initialActions[r.fila] = {
            action: "",
            cliente_id: "",
          };
        }
      }
    });
    setSelectedActions(initialActions);
  }, [open, importResult, duplicates]);

  // Contadores calculados en tiempo real desde las filas actuales
  const counters = React.useMemo(() => {
    const total = importResult?.total ?? rows.length;
    let clientes_creados = 0;
    let consultas_agregadas = 0;
    let repetidas = 0;
    let conflictos = 0;
    let errores = 0;
    let descartados = 0;

    rows.forEach((r) => {
      if (r.estado === "CLIENTE_CREADO") clientes_creados++;
      else if (r.estado === "CONSULTA_AGREGADA") consultas_agregadas++;
      else if (r.estado === "CONSULTA_REPETIDA") repetidas++;
      else if (r.estado === "CONFLICTO_PENDIENTE") conflictos++;
      else if (r.estado === "DESCARTADO") descartados++;
      else if (r.estado === "ERROR_VALIDACION" || r.estado === "ERROR_PERSISTENCIA") errores++;
    });

    const consultas_creadas = clientes_creados + consultas_agregadas;

    return {
      total,
      filas_validas: Math.max(total - errores, 0),
      filas_invalidas: errores,
      clientes_creados,
      consultas_creadas,
      consultas_agregadas,
      repetidas,
      conflictos,
      errores,
      descartados,
    };
  }, [importResult, rows]);

  const isResolving = resolvingAll || Object.values(loadingRows).some(Boolean);

  const getPersonaGroupRows = (row, sourceRows = rows) => {
    const groupKey = personaGroupKey(row);
    return sourceRows.filter(
      (candidate) =>
        candidate.estado === "CONFLICTO_PENDIENTE" && personaGroupKey(candidate) === groupKey
    );
  };

  const handleActionChange = (fila, value) => {
    if (value === "CREAR_PERSONA") {
      const selectedRow = rows.find((row) => row.fila === fila);
      const groupRows = selectedRow ? getPersonaGroupRows(selectedRow) : [];
      setSelectedActions((prev) => ({
        ...prev,
        ...Object.fromEntries(
          groupRows.map((row) => [row.fila, { action: "CREAR_PERSONA", cliente_id: null }])
        ),
      }));
    } else if (value === "DESCARTAR") {
      setSelectedActions((prev) => ({
        ...prev,
        [fila]: { action: value, cliente_id: null },
      }));
    } else if (value.startsWith("AGREGAR_")) {
      const clienteId = Number(value.replace("AGREGAR_", ""));
      setSelectedActions((prev) => ({
        ...prev,
        [fila]: { action: "AGREGAR_CONSULTA", cliente_id: clienteId },
      }));
    } else {
      setSelectedActions((prev) => ({
        ...prev,
        [fila]: { action: "", cliente_id: "" },
      }));
    }

    setRowErrors((prev) => ({ ...prev, [fila]: null }));
  };

  const executeResolveRow = async (row, actionConfig) => {
    setLoadingRows((prev) => ({ ...prev, [row.fila]: true }));
    setRowErrors((prev) => ({ ...prev, [row.fila]: null }));

    try {
      const payload = {
        fila: row.fila,
        action: actionConfig.action,
        cliente_id: actionConfig.cliente_id || null,
        nombre: row.nombre,
        telefono: row.telefono,
        email: row.email || null,
        consulta: row.consulta || null,
        obtenido_de: row.obtenido_de || null,
        observacion: row.observacion || null,
        idempotency_key: row.detalles?.idempotency_key || null,
      };

      const res = await apiFetch("/api/clientes/import-csv/resolve-conflict", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let detail = "Error al resolver conflicto.";
        try {
          const errData = await res.json();
          if (errData?.detail) detail = errData.detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const resData = await res.json();

      setRows((prevRows) =>
        prevRows.map((r) =>
          r.fila === row.fila
            ? {
                ...r,
                estado: resData.estado,
                mensaje: resData.mensaje,
                cliente_id: resData.cliente_id,
                consulta_id: resData.consulta_id,
                email: resData.email || r.email,
                obtenido_de: resData.obtenido_de || r.obtenido_de,
                observacion: resData.observacion || r.observacion,
              }
            : r
        )
      );

    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [row.fila]: err?.message || "Error de red al intentar resolver.",
      }));
    } finally {
      setLoadingRows((prev) => ({ ...prev, [row.fila]: false }));
    }
  };

  const executeResolvePersonaGroup = async (groupRows) => {
    if (!groupRows.length) return;

    const representative = groupRows[0];
    const loadingState = Object.fromEntries(groupRows.map((row) => [row.fila, true]));
    const clearedErrors = Object.fromEntries(groupRows.map((row) => [row.fila, null]));
    setLoadingRows((prev) => ({ ...prev, ...loadingState }));
    setRowErrors((prev) => ({ ...prev, ...clearedErrors }));

    try {
      const payload = {
        fila: representative.fila,
        action: "CREAR_PERSONA",
        cliente_id: null,
        nombre: representative.nombre,
        telefono: representative.telefono,
        email: representative.email || null,
        consulta: representative.consulta || null,
        obtenido_de: representative.obtenido_de || null,
        observacion: representative.observacion || null,
        idempotency_key: representative.detalles?.idempotency_key || null,
        filas_grupo: groupRows.map((row) => ({
          fila: row.fila,
          nombre: row.nombre,
          telefono: row.telefono,
          email: row.email || null,
          consulta: row.consulta,
          obtenido_de: row.obtenido_de || null,
          observacion: row.observacion || null,
        })),
      };

      const res = await apiFetch("/api/clientes/import-csv/resolve-conflict", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let detail = "Error al crear la persona y sus consultas.";
        try {
          const errData = await res.json();
          if (errData?.detail) detail = errData.detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const resData = await res.json();
      const resolvedRows = Array.isArray(resData.resultados) ? resData.resultados : [resData];
      const resolvedByRow = new Map(resolvedRows.map((row) => [row.fila, row]));

      setRows((prevRows) =>
        prevRows.map((row) => {
          const resolved = resolvedByRow.get(row.fila);
          if (!resolved) return row;
          return {
            ...row,
            ...resolved,
            email: resolved.email || row.email,
            obtenido_de: resolved.obtenido_de || row.obtenido_de,
            observacion: resolved.observacion || row.observacion,
          };
        })
      );
    } catch (err) {
      const message = err?.message || "Error de red al intentar crear la persona.";
      setRowErrors((prev) => ({
        ...prev,
        ...Object.fromEntries(groupRows.map((row) => [row.fila, message])),
      }));
    } finally {
      setLoadingRows((prev) => ({
        ...prev,
        ...Object.fromEntries(groupRows.map((row) => [row.fila, false])),
      }));
    }
  };

  const handleResolveClick = (row) => {
    const actionConfig = selectedActions[row.fila];
    if (!actionConfig || !actionConfig.action) return;

    if (actionConfig.action === "AGREGAR_CONSULTA" && !actionConfig.cliente_id) {
      setRowErrors((prev) => ({
        ...prev,
        [row.fila]: "Por favor seleccioná un cliente destino.",
      }));
      return;
    }

    if (actionConfig.action === "CREAR_PERSONA") {
      setConfirmPersonaModal({
        open: true,
        rows: getPersonaGroupRows(row),
        actionConfig,
      });
      return;
    }

    executeResolveRow(row, actionConfig);
  };

  const handleConfirmPersona = () => {
    const { rows: personaRows, actionConfig } = confirmPersonaModal;
    setConfirmPersonaModal({ open: false, rows: [], actionConfig: null });
    if (personaRows.length && actionConfig) {
      executeResolvePersonaGroup(personaRows);
    }
  };

  const startResolveAll = async () => {
    const pendingRows = rows.filter((r) => r.estado === "CONFLICTO_PENDIENTE");
    const personaRows = pendingRows.filter(
      (r) => selectedActions[r.fila]?.action === "CREAR_PERSONA"
    );

    if (personaRows.length > 0) {
      setConfirmBulkPersonaModal({
        open: true,
        personaRows,
      });
      return;
    }

    await executeBulkResolve(pendingRows);
  };

  const executeBulkResolve = async (pendingRows) => {
    setResolvingAll(true);
    const processedRows = new Set();
    try {
      for (const r of pendingRows) {
        if (processedRows.has(r.fila)) continue;
        const groupRows = getPersonaGroupRows(r, pendingRows);
        const personaActionSelected = groupRows.some(
          (row) => selectedActions[row.fila]?.action === "CREAR_PERSONA"
        );
        if (personaActionSelected) {
          groupRows.forEach((row) => processedRows.add(row.fila));
          await executeResolvePersonaGroup(groupRows);
          continue;
        }

        const actionConfig = selectedActions[r.fila];
        if (actionConfig && actionConfig.action) {
          if (actionConfig.action === "AGREGAR_CONSULTA" && !actionConfig.cliente_id) {
            continue;
          }
          await executeResolveRow(r, actionConfig);
          processedRows.add(r.fila);
        }
      }
    } finally {
      setResolvingAll(false);
    }
  };

  const handleConfirmBulkPersona = async () => {
    const pendingRows = rows.filter((r) => r.estado === "CONFLICTO_PENDIENTE");
    setConfirmBulkPersonaModal({ open: false, personaRows: [] });
    await executeBulkResolve(pendingRows);
  };

  const handleDownloadReport = (onlyFiltered = false) => {
    setDownloadMenuAnchor(null);
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return "";
      let str = String(val);
      if (/^[\s]*[=+\-@\t\r]/.test(str)) {
        str = "'" + str;
      }
      const escaped = str.replace(/"/g, '""');
      if (/[",\n\r]/.test(escaped)) return `"${escaped}"`;
      return escaped;
    };

    const targetRows = onlyFiltered
      ? rows.filter((r) =>
          ["CONFLICTO_PENDIENTE", "ERROR_VALIDACION", "ERROR_PERSISTENCIA", "DESCARTADO"].includes(r.estado)
        )
      : rows;

    const headers = ["Fila", "Estado", "Mensaje", "Nombre", "Teléfono", "Email", "Consulta", "Origen", "Observación", "Campo Error"];
    const tableRows = targetRows.map((r) => [
      r.fila,
      r.estado,
      r.mensaje,
      r.nombre || "",
      r.telefono || "",
      r.email || "",
      r.consulta || "",
      r.obtenido_de || "",
      r.observacion || "",
      r.detalles?.campo || "",
    ]);

    const csvContent = [headers, ...tableRows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = onlyFiltered ? "reporte_errores_conflictos.csv" : "reporte_importacion_completo.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleRequestClose = () => {
    if (isResolving) return;
    if (counters.conflictos > 0) {
      setConfirmCloseModal(true);
    } else {
      completeClose();
    }
  };

  const completeClose = () => {
    if (isResolving) return;
    setConfirmCloseModal(false);
    if (!refreshedRef.current) {
      refreshedRef.current = true;
      if (onRefresh) {
        onRefresh(counters);
      }
    }
    if (onClose) onClose(counters);
  };

  const getStatusChip = (estado) => {
    switch (estado) {
      case "CLIENTE_CREADO":
        return <Chip label="Cliente Creado" color="success" size="small" icon={<CheckCircleOutlineIcon />} />;
      case "CONSULTA_AGREGADA":
        return <Chip label="Consulta Agregada" color="success" size="small" icon={<AddCircleOutlineIcon />} />;
      case "CONSULTA_REPETIDA":
        return <Chip label="Consulta Repetida" color="info" size="small" />;
      case "CONFLICTO_PENDIENTE":
        return <Chip label="Conflicto Pendiente" color="warning" size="small" icon={<WarningAmberIcon />} />;
      case "DESCARTADO":
        return <Chip label="Descartado" color="default" size="small" icon={<DeleteOutlineIcon />} />;
      case "ERROR_VALIDACION":
      case "ERROR_PERSISTENCIA":
        return <Chip label="Error" color="error" size="small" icon={<ErrorOutlineIcon />} />;
      default:
        return <Chip label={estado} size="small" />;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleRequestClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Resultado de Importación y Resolución de Conflictos
            </Typography>
            <IconButton onClick={handleRequestClose} size="small" disabled={isResolving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Tarjetas de Resumen en Tiempo Real */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1.2, mb: 2.5 }}>
            <Paper variant="outlined" sx={{ p: 1.2, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="h6">{counters.total}</Typography>
              <Typography variant="caption" color="success.main" display="block">
                Válidas: {counters.filas_validas}
              </Typography>
              <Typography variant="caption" color="error.main" display="block">
                Inválidas: {counters.filas_invalidas}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.2, textAlign: "center", bgcolor: "#f6ffed", borderColor: "#b7eb8f" }}>
              <Typography variant="caption" color="success.main">Clientes Creados</Typography>
              <Typography variant="h6" color="success.main">{counters.clientes_creados}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.2, textAlign: "center", bgcolor: "#e6f7ff", borderColor: "#91d5ff" }}>
              <Typography variant="caption" color="primary.main">Consultas Creadas</Typography>
              <Typography variant="h6" color="primary.main">{counters.consultas_creadas}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.2, textAlign: "center", bgcolor: "#f0f5ff", borderColor: "#adc6ff" }}>
              <Typography variant="caption" color="secondary.main">Consultas Agregadas</Typography>
              <Typography variant="h6" color="secondary.main">{counters.consultas_agregadas}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.2, textAlign: "center", bgcolor: "#fffbe6", borderColor: "#ffe58f" }}>
              <Typography variant="caption" color="warning.main">Repetidas</Typography>
              <Typography variant="h6" color="warning.main">{counters.repetidas}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.2, textAlign: "center", bgcolor: counters.conflictos > 0 ? "#fff2e8" : "background.paper", borderColor: counters.conflictos > 0 ? "#ffbb96" : "divider" }}>
              <Typography variant="caption" color={counters.conflictos > 0 ? "warning.dark" : "text.secondary"}>Conflictos</Typography>
              <Typography variant="h6" color={counters.conflictos > 0 ? "warning.dark" : "text.primary"}>{counters.conflictos}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.2, textAlign: "center", bgcolor: counters.errores > 0 ? "#fff1f0" : "background.paper", borderColor: counters.errores > 0 ? "#ffa39e" : "divider" }}>
              <Typography variant="caption" color={counters.errores > 0 ? "error.main" : "text.secondary"}>Errores</Typography>
              <Typography variant="h6" color={counters.errores > 0 ? "error.main" : "text.primary"}>{counters.errores}</Typography>
            </Paper>
          </Box>

          {/* Acciones globales */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                size="small"
              >
                Descargar Reporte (CSV)
              </Button>
              <Menu
                anchorEl={downloadMenuAnchor}
                open={Boolean(downloadMenuAnchor)}
                onClose={() => setDownloadMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleDownloadReport(false)}>
                  Reporte Completo
                </MenuItem>
                <MenuItem onClick={() => handleDownloadReport(true)}>
                  Solo Errores y Conflictos
                </MenuItem>
              </Menu>
            </Box>

            {counters.conflictos > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={resolvingAll ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                onClick={startResolveAll}
                disabled={resolvingAll}
                size="small"
              >
                {resolvingAll ? "Resolviendo..." : "Resolver Seleccionados"}
              </Button>
            )}
          </Box>

          {/* Tabla principal por fila */}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 50 }}><strong>Fila</strong></TableCell>
                  <TableCell style={{ width: 140 }}><strong>Nombre</strong></TableCell>
                  <TableCell style={{ width: 110 }}><strong>Teléfono</strong></TableCell>
                  <TableCell style={{ width: 140 }}><strong>Consulta</strong></TableCell>
                  <TableCell style={{ width: 140 }}><strong>Estado</strong></TableCell>
                  <TableCell><strong>Mensaje / Resolución</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const isConflict = r.estado === "CONFLICTO_PENDIENTE";
                  const existentes = r.detalles?.clientes_existentes || [];
                  const actionConfig = selectedActions[r.fila] || { action: "", cliente_id: "" };
                  const loading = loadingRows[r.fila] || false;
                  const rowErr = rowErrors[r.fila];

                  let selectValue = actionConfig.action;
                  if (actionConfig.action === "AGREGAR_CONSULTA" && actionConfig.cliente_id) {
                    selectValue = `AGREGAR_${actionConfig.cliente_id}`;
                  }

                  return (
                    <TableRow key={r.fila} hover sx={{ bgcolor: isConflict ? "rgba(255, 247, 230, 0.4)" : "inherit" }}>
                      <TableCell>{r.fila}</TableCell>
                      <TableCell><strong>{r.nombre || "—"}</strong></TableCell>
                      <TableCell>{r.telefono || "—"}</TableCell>
                      <TableCell>{r.consulta || "—"}</TableCell>
                      <TableCell>{getStatusChip(r.estado)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="body2" color={rowErr ? "error" : "text.primary"}>
                            {rowErr || r.mensaje}
                          </Typography>

                          {isConflict && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <FormControl size="small" sx={{ minWidth: 280 }}>
                                  <Select
                                    value={selectValue}
                                    onChange={(e) => handleActionChange(r.fila, e.target.value)}
                                    displayEmpty
                                    disabled={loading}
                                  >
                                    <MenuItem value="" disabled>
                                      <em>Seleccionar acción...</em>
                                    </MenuItem>
                                    {existentes.map((c) => (
                                      <MenuItem key={c.id} value={`AGREGAR_${c.id}`}>
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                          <span>Agregar consulta a: <strong>{c.nombre} (ID: {c.id})</strong></span>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                    <MenuItem value="CREAR_PERSONA">
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <PersonAddIcon fontSize="small" color="action" />
                                        <span>Es otra persona (Crear nuevo cliente)</span>
                                      </Box>
                                    </MenuItem>
                                    <MenuItem value="DESCARTAR">
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <DeleteOutlineIcon fontSize="small" color="error" />
                                        <span>Descartar fila</span>
                                      </Box>
                                    </MenuItem>
                                  </Select>
                                </FormControl>

                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={loading || !actionConfig.action || (actionConfig.action === "AGREGAR_CONSULTA" && !actionConfig.cliente_id)}
                                  onClick={() => handleResolveClick(r)}
                                  startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                                >
                                  {loading ? "Guardando..." : "Resolver"}
                                </Button>
                              </Box>

                              {/* Detalle ordenado de consultas existentes para ayudar a decidir */}
                              {existentes.length > 0 && (
                                <Box sx={{ mt: 0.5, p: 1, bgcolor: "#fafafa", borderRadius: 1, border: "1px solid #f0f0f0" }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                    Clientes existentes coincidentes:
                                  </Typography>
                                  {existentes.map((c) => (
                                    <Box key={c.id} sx={{ mt: 0.5, pl: 1, borderLeft: "2px solid #1890ff" }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        #{c.id} - {c.nombre} ({c.telefono})
                                      </Typography>
                                      {c.consultas && c.consultas.length > 0 ? (
                                        c.consultas.map((cons, idx) => (
                                          <Typography key={cons.id || idx} variant="caption" display="block" color="text.secondary" sx={{ pl: 1 }}>
                                            • Vehículo: <strong>{cons.vehiculo || "Sin especificar"}</strong> | Estado: <strong>{cons.estado || "—"}</strong> | Vendedor: <strong>{cons.vendedor || "sin asignar"}</strong> {cons.fecha ? `| Fecha: ${cons.fecha.substring(0, 10)}` : ""}
                                          </Typography>
                                        ))
                                      ) : (
                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ pl: 1 }}>
                                          • Sin consultas registradas
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button onClick={handleRequestClose} variant="contained" disabled={isResolving}>
            {counters.conflictos > 0 ? "Cerrar (Con Conflictos)" : "Terminar y Cerrar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación para CREAR_PERSONA individual */}
      <Dialog
        open={confirmPersonaModal.open}
        onClose={() => setConfirmPersonaModal({ open: false, rows: [], actionConfig: null })}
        maxWidth="xs"
      >
        <DialogTitle>Confirmar Creación de Persona</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Compartir el teléfono de un cliente existente debe ser confirmado explícitamente.
          </Alert>
          <Typography variant="body2">
            ¿Confirmás que <strong>{confirmPersonaModal.rows.length} fila(s)</strong> de <strong>{confirmPersonaModal.rows[0]?.nombre}</strong> pertenecen a una persona distinta a la existente con el teléfono <strong>{confirmPersonaModal.rows[0]?.telefono}</strong>?
            Se creará un único cliente nuevo con todas sus consultas diferentes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPersonaModal({ open: false, rows: [], actionConfig: null })}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmPersona} variant="contained" color="warning">
            Sí, Crear Persona Distinta
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación masiva para CREAR_PERSONA */}
      <Dialog
        open={confirmBulkPersonaModal.open}
        onClose={() => setConfirmBulkPersonaModal({ open: false, personaRows: [] })}
        maxWidth="sm"
      >
        <DialogTitle>Confirmar Creación Masiva de Personas</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Las siguientes filas están configuradas para crear personas nuevas compartiendo teléfono con clientes existentes. Las filas con el mismo teléfono y nombre se agruparán en una sola persona.
          </Alert>
          <Box sx={{ maxHeight: 200, overflowY: "auto", mb: 1 }}>
            {confirmBulkPersonaModal.personaRows.map((r) => (
              <Typography key={r.fila} variant="body2" sx={{ py: 0.5 }}>
                • Fila #{r.fila}: <strong>{r.nombre}</strong> (Tel: {r.telefono})
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            ¿Deseás continuar creando estos grupos como personas distintas?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkPersonaModal({ open: false, personaRows: [] })}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmBulkPersona} variant="contained" color="warning">
            Sí, Confirmar Creación Masiva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación al cerrar con conflictos pendientes */}
      <Dialog
        open={confirmCloseModal}
        onClose={() => setConfirmCloseModal(false)}
        maxWidth="xs"
      >
        <DialogTitle>Conflictos Pendientes</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Aún quedan <strong>{counters.conflictos}</strong> fila(s) con conflictos pendientes sin resolver.
            ¿Estás seguro de que deseás cerrar la ventana? Las filas resueltas hasta el momento permanecerán guardadas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCloseModal(false)}>
            Seguir Resolviendo
          </Button>
          <Button onClick={completeClose} variant="contained" color="primary">
            Sí, Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
